import { PlusOutlined, QuestionCircleOutlined } from '@ant-design/icons'
import { Button, Form, Input, Select, Switch, Table, TableColumnsType, Tooltip, Typography, message } from 'antd'
import type { SorterResult, TablePaginationConfig } from 'antd/es/table/interface'
import { SearchStakingErc3643PoolsParams } from 'Modules'
import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { stakingErc3643Api } from 'api/staking-erc3643-api'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import ProtectedButton from 'component/button/protected-button'
import ButtonCopy from 'component/button/button-copy'
import LinkAddress from 'component/link/link-address'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { FEATURE } from 'constants/feature'
import { DEFAULT_PAGE_OPTIONS, DEFAULT_PAGE_SIZE } from 'constants/pagination'
import { STAKING_ERC3643_STATUS_TO_API } from 'constants/staking-erc3643'
import { mapOverview, mapPoolToRow } from 'extends/staking-erc3643.extend'
import { useStakingErc3643RewardTokens } from 'hooks/staking-erc3643/use-staking-erc3643-reward-tokens'
import useSearchParams from 'hooks/use-search-params'
import { PAGES } from 'routes'
import { logError } from 'utils/logger'

import OverviewCards from './components/overview-cards'
import PoolFormModal from './components/pool-form-modal'
import { PoolListOverview, PoolStatus, RewardTokenType, StakingPoolRow } from './types'

import './style.scss'

const { Text } = Typography

const STATUS_OPTIONS: Array<PoolStatus | 'All statuses'> = [
  'All statuses',
  'Scheduled',
  'Active',
  'Ended',
]

const SORT_ORDER_MAP: Record<string, string> = {
  ascend: 'asc',
  descend: 'desc',
}

const statusClass: Record<PoolStatus, string> = {
  Scheduled: 'status-pending',
  Active: 'status-success',
  Ended: 'status-fail',
}

/** PTO stake count — ERC-3643 PTO has 0 decimals (SRS: integer + thousand separators). */
const formatPtoAmount = (value: number) =>
  new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value)

const formatApr = (apr: number | null) => (apr === null ? '-' : `${apr}%`)

/** FIXED_REWARD pools show reward tokens per PTO per year; legacy APR pools keep the % rate. */
const formatPoolRate = (record: StakingPoolRow) => {
  if (record.rewardModel !== 'FIXED_REWARD') return formatApr(record.apr)
  if (record.rewardPerYear === null) return '-'
  const amount = new Intl.NumberFormat('en-US', { maximumFractionDigits: 18 }).format(
    record.rewardPerYear
  )
  return `${amount} ${record.rewardToken} / PTO / year`
}

const emptyOverview: PoolListOverview = {
  totalValueStakedUsd: 0,
  totalStakers: 0,
  rewardClaimed: [],
  rewardUnclaimed: [],
}

const ALL_REWARD_TOKENS = ''

type PoolFilterFormValues = {
  keyword?: string
  status?: PoolStatus | 'All statuses'
  rewardToken?: string
}

const buildPoolListSearchParams = (
  prev: SearchStakingErc3643PoolsParams,
  values: PoolFilterFormValues,
  overrides?: Partial<SearchStakingErc3643PoolsParams>
): SearchStakingErc3643PoolsParams => {
  const next: SearchStakingErc3643PoolsParams = {
    page: overrides?.page ?? 1,
    limit: overrides?.limit ?? prev.limit ?? DEFAULT_PAGE_SIZE,
  }
  if (overrides?.sort ?? prev.sort) {
    next.sort = overrides?.sort ?? prev.sort
  }

  const keyword = (values.keyword ?? '').trim()
  if (keyword) {
    next.keyword = keyword
  }

  if (values.status && values.status !== 'All statuses') {
    next.status = STAKING_ERC3643_STATUS_TO_API[values.status]
  }
  if (values.rewardToken && values.rewardToken !== ALL_REWARD_TOKENS) {
    next.rewardToken = values.rewardToken
  }

  return next
}

const StakingErc3643Page = () => {
  const navigate = useNavigate()
  const [filterForm] = Form.useForm()
  const { searchParams, setSearchParams, initPagination } =
    useSearchParams<SearchStakingErc3643PoolsParams>()

  const [pools, setPools] = useState<StakingPoolRow[]>([])
  const [totalResults, setTotalResults] = useState(0)
  const [totalPools, setTotalPools] = useState(0)
  const [overview, setOverview] = useState<PoolListOverview>(emptyOverview)
  const { rewardTokens, loadRewardTokens, loadingRewardTokens } = useStakingErc3643RewardTokens()
  const [loading, setLoading] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [editPool, setEditPool] = useState<StakingPoolRow | null>(null)

  const loadOverview = useCallback(async () => {
    try {
      const res = await stakingErc3643Api.getOverview()
      if (res.data?.success && res.data.data) {
        setOverview(mapOverview(res.data.data))
      }
    } catch (error) {
      logError('Load staking overview failed', error)
      setOverview(emptyOverview)
    }
  }, [])

  const loadPools = useCallback(async (params: SearchStakingErc3643PoolsParams) => {
    setLoading(true)
    try {
      const res = await stakingErc3643Api.getPools(params)
      if (res.data?.success && res.data.data) {
        setPools((res.data.data.results ?? []).map(mapPoolToRow))
        setTotalResults(res.data.data.totalResults ?? 0)
        setTotalPools(res.data.data.total ?? 0)
      } else {
        setPools([])
        setTotalResults(0)
      }
    } catch (error) {
      logError('Load staking pools failed', error)
      setPools([])
      setTotalResults(0)
    } finally {
      setLoading(false)
    }
  }, [])

  const refreshAll = useCallback(() => {
    loadPools(searchParams)
    loadOverview()
    loadRewardTokens()
  }, [loadPools, loadOverview, loadRewardTokens, searchParams])

  useEffect(() => {
    loadOverview()
  }, [loadOverview])

  useEffect(() => {
    loadPools(searchParams)
  }, [loadPools, searchParams])

  const rewardTokenFilterOptions = useMemo(
    () => [
      { value: ALL_REWARD_TOKENS, label: 'All reward tokens' },
      ...rewardTokens.map((t) => ({
        value: t.address,
        label: t.symbol as RewardTokenType,
      })),
    ],
    [rewardTokens]
  )

  const applyFilters = useCallback(
    (overrides?: Partial<SearchStakingErc3643PoolsParams>) => {
      const values = filterForm.getFieldsValue() as PoolFilterFormValues
      setSearchParams((prev) => buildPoolListSearchParams(prev, values, overrides))
    },
    [filterForm, setSearchParams]
  )

  const handleDisplayChange = async (pool: StakingPoolRow, isDisplay: boolean) => {
    try {
      const res = await stakingErc3643Api.updatePoolDisplay(pool.id, isDisplay)
      if (res.data?.success) {
        setPools((prev) => prev.map((p) => (p.id === pool.id ? { ...p, display: isDisplay } : p)))
      }
    } catch (error) {
      logError('Update display failed', error)
      message.error('Failed to update display')
    }
  }

  const columns: TableColumnsType<StakingPoolRow> = [
    {
      title: 'PTO Name',
      dataIndex: 'ptoSymbol',
      key: 'ptoSymbol',
      sorter: true,
      align: 'left',
      render: (_, record) => (
        <div className="staking-3643-pto-cell">
          <Text strong>{record.ptoSymbol}</Text>
          <Text type="secondary">{record.ptoName}</Text>
        </div>
      ),
    },
    {
      title: 'Contract Address',
      dataIndex: 'contractAddress',
      key: 'contractAddress',
      render: (text: string) => (
        <div className="staking-3643-address-cell">
          <LinkAddress address={text} type="address" />
          {text ? <ButtonCopy text={text} /> : null}
        </div>
      ),
    },
    {
      title: 'Reward Token',
      dataIndex: 'rewardToken',
      key: 'rewardToken',
    },
    {
      title: 'Reward Per PTO/Year',
      dataIndex: 'rewardPerYear',
      key: 'rewardPerYear',
      sorter: true,
      render: (_, record) => <span className="color-apy">{formatPoolRate(record)}</span>,
    },
    {
      title: 'Total Stakers',
      dataIndex: 'totalStakers',
      key: 'totalStakers',
    },
    {
      title: 'Staked Amount',
      key: 'stakedAmount',
      render: (_, record) => `${formatPtoAmount(record.stakedAmount)} ${record.stakedSymbol}`,
    },
    {
      title: 'Start Date - End Date',
      key: 'dateRange',
      render: (_, record) =>
        `${dayjs(record.startDate).format('DD/MM/YYYY')} → ${dayjs(record.endDate).format('DD/MM/YYYY')}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value: PoolStatus) => <span className={statusClass[value]}>{value}</span>,
    },
    {
      title: (
        <span className="staking-3643-display-head">
          Display
          <Tooltip title="Switch to On to display staking pool, or switch to Off to hide staking pool on user website">
            <QuestionCircleOutlined />
          </Tooltip>
        </span>
      ),
      key: 'display',
      render: (_, record) => (
        <div className="staking-3643-display-cell">
          <Switch checked={record.display} onChange={(next) => handleDisplayChange(record, next)} />
          <span>{record.display ? 'Show' : 'Hide'}</span>
        </div>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      render: (value: string) => dayjs(value).format('DD/MM/YYYY HH:mm:ss'),
    },
    {
      title: 'Action',
      key: 'action',
      width: 160,
      render: (_, record) => (
        <div className="btn-group">
          <Button
            className="button-detail"
            onClick={() =>
              navigate(PAGES.STAKING_ERC3643_POOL_DETAIL.replace(':poolId', record.id))
            }
          >
            Detail
          </Button>
          {record.status !== 'Ended' ? (
            <ProtectedButton
              className="button-edit"
              feature={FEATURE.STAKING_NFT}
              requireCRUD
              onClick={() => setEditPool(record)}
            >
              Edit
            </ProtectedButton>
          ) : null}
        </div>
      ),
    },
  ]

  const onTableChange = (
    pagination: TablePaginationConfig,
    _filters: unknown,
    sorter: SorterResult<StakingPoolRow> | SorterResult<StakingPoolRow>[]
  ) => {
    const single = Array.isArray(sorter) ? sorter[0] : sorter
    let sort = searchParams.sort
    if (single?.field && single.order) {
      const field =
        single.field === 'ptoSymbol'
          ? 'ptoName'
          : single.field === 'createdAt'
            ? 'createdAt'
            : single.field === 'rewardPerYear'
              ? 'rewardPerYear'
              : 'ptoName'
      sort = `${field}:${SORT_ORDER_MAP[single.order] ?? 'asc'}`
    }
    applyFilters({
      page: pagination.current ?? 1,
      limit: pagination.pageSize ?? searchParams.limit ?? DEFAULT_PAGE_SIZE,
      sort,
    })
  }

  return (
    <div className="main-section staking staking-erc-3643">
      <Breadcrumb additionalItems={[BREADCRUMBS.STAKING_ERC3643]} />

      <OverviewCards data={overview} />

      <div className="main-title">
        Staking ERC-3643
        <span className="new-button">
          <ProtectedButton
            type="primary"
            icon={<PlusOutlined />}
            feature={FEATURE.STAKING_NFT}
            requireCRUD
            onClick={() => setCreateOpen(true)}
          >
            New Pool
          </ProtectedButton>
        </span>
      </div>

      <div className="table-row">
        <div className="main-table-row">
          <Form
            form={filterForm}
            className="staking-3643-filter-form"
            layout="inline"
            onFinish={(values) => {
              setSearchParams((prev) =>
                buildPoolListSearchParams(prev, values as PoolFilterFormValues, { page: 1 })
              )
            }}
            onValuesChange={(changed, allValues) => {
              if (Object.prototype.hasOwnProperty.call(changed, 'keyword')) {
                return
              }
              setSearchParams((prev) =>
                buildPoolListSearchParams(prev, allValues as PoolFilterFormValues, { page: 1 })
              )
            }}
            initialValues={{
              status: 'All statuses',
              rewardToken: ALL_REWARD_TOKENS,
            }}
          >
            <Form.Item name="keyword">
              <Input
                allowClear
                placeholder="Search by PTO name, symbol, contract address"
                style={{ minWidth: 320 }}
              />
            </Form.Item>
            <Form.Item name="status">
              <Select
                style={{ minWidth: 150 }}
                options={STATUS_OPTIONS.map((item) => ({ value: item, label: item }))}
              />
            </Form.Item>
            <Form.Item name="rewardToken">
              <Select
                style={{ minWidth: 170 }}
                options={rewardTokenFilterOptions}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit">
                Search
              </Button>
            </Form.Item>
            <Form.Item>
              <Button
                onClick={() => {
                  filterForm.resetFields()
                  setSearchParams({
                    page: 1,
                    limit: DEFAULT_PAGE_SIZE,
                  } as SearchStakingErc3643PoolsParams)
                }}
              >
                Reset
              </Button>
            </Form.Item>
            <Form.Item className="staking-3643-filter-count">
              <span className="main-color-table">
                {totalResults} of {totalPools} pools
              </span>
            </Form.Item>
          </Form>

          <Table<StakingPoolRow>
            rowKey="id"
            columns={columns}
            dataSource={pools}
            loading={loading}
            className="main-table"
            scroll={{ x: 'max-content' }}
            locale={{ emptyText: 'No staking pools match the current filters.' }}
            pagination={{
              ...initPagination(totalResults),
              pageSizeOptions: DEFAULT_PAGE_OPTIONS,
              showSizeChanger: true,
              showTotal: (total, range) => `Showing ${range[0]}-${range[1]} of ${total} pools`,
            }}
            onChange={onTableChange}
          />
        </div>
      </div>

      <PoolFormModal
        open={createOpen}
        mode="create"
        rewardTokens={rewardTokens}
        loadingRewardTokens={loadingRewardTokens}
        onClose={() => setCreateOpen(false)}
        onSuccess={refreshAll}
      />
      <PoolFormModal
        open={Boolean(editPool)}
        mode="edit"
        pool={editPool}
        rewardTokens={rewardTokens}
        loadingRewardTokens={loadingRewardTokens}
        onClose={() => setEditPool(null)}
        onSuccess={refreshAll}
      />
    </div>
  )
}

export default StakingErc3643Page
